import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/adminService';

const LoginPage = () => {
  const { signIn, resetPasswordForEmail, updatePassword, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);

  const isRecovery = typeof window !== 'undefined' && window.location.hash.includes('type=recovery');

  useEffect(() => {
    if (isRecovery) setShowSetPassword(true);
  }, [isRecovery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await signIn(email.trim(), password);
    setLoading(false);
    if (err) {
      setError(err.message || 'Login failed');
      return;
    }
    const isAdmin = await adminService.isAdmin();
    navigate(isAdmin ? '/admin' : '/dashboard', { replace: true });
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await resetPasswordForEmail(email.trim());
    setLoading(false);
    if (err) {
      setError(err.message || 'Could not send reset email');
      return;
    }
    setForgotSent(true);
  };

  const handleSetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setError('');
    setSetPasswordLoading(true);
    const { error: err } = await updatePassword(newPassword);
    setSetPasswordLoading(false);
    if (err) {
      setError(err.message || 'Could not update password');
      return;
    }
    window.history.replaceState(null, '', window.location.pathname);
    setShowSetPassword(false);
    setNewPassword('');
    const isAdmin = await adminService.isAdmin();
    navigate(isAdmin ? '/admin' : '/dashboard', { replace: true });
  };

  if (showSetPassword) {
    if (!user) {
      return (
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md rounded-2xl border border-slate-600 bg-slate-800/80 p-8 text-center text-slate-400">
            Loading...
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-2xl border border-slate-600 bg-slate-800/80 p-8">
          <h1 className="font-heading font-bold text-2xl text-white text-center mb-2">Set new password</h1>
          <p className="text-slate-400 text-sm text-center mb-6">Choose a new password for your account.</p>
          <form onSubmit={handleSetPasswordSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-xl bg-red-500/20 text-red-300 text-sm border border-red-500/30">{error}</div>
            )}
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-slate-300 mb-1">New password</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-2 rounded-xl border border-slate-600 bg-slate-700/50 text-white placeholder-slate-500 focus:ring-2 focus:ring-brand-pink/50 focus:border-brand-pink outline-none"
                placeholder="At least 6 characters"
              />
            </div>
            <button
              type="submit"
              disabled={setPasswordLoading}
              className="w-full py-3 rounded-xl bg-brand-pink text-white font-semibold hover:bg-brand-pink-dark disabled:opacity-50 transition-colors"
            >
              {setPasswordLoading ? 'Updating...' : 'Set password and continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-600 bg-slate-800/80 p-8">
        <h1 className="font-heading font-bold text-2xl text-white text-center mb-8">
          {showForgot ? 'Reset password' : 'Login'}
        </h1>

        {!showForgot ? (
          <>
            <p className="text-slate-400 text-sm text-center mb-4">
              Existing user? Use &quot;Forgot password&quot; below to set your password and log in.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-xl bg-red-500/20 text-red-300 text-sm border border-red-500/30">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 rounded-xl border border-slate-600 bg-slate-700/50 text-white placeholder-slate-500 focus:ring-2 focus:ring-brand-pink/50 focus:border-brand-pink outline-none"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 rounded-xl border border-slate-600 bg-slate-700/50 text-white placeholder-slate-500 focus:ring-2 focus:ring-brand-pink/50 focus:border-brand-pink outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl bg-brand-pink text-white font-semibold hover:bg-brand-pink-dark disabled:opacity-50 transition-colors"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
            <p className="mt-4 text-center">
              <button
                type="button"
                onClick={() => { setShowForgot(true); setError(''); setForgotSent(false); }}
                className="text-slate-400 hover:text-brand-pink text-sm"
              >
                Forgot password?
              </button>
            </p>
          </>
        ) : (
          <>
            {forgotSent ? (
              <div className="p-4 rounded-xl bg-emerald-500/20 text-emerald-300 text-sm border border-emerald-500/30 text-center">
                Check your email for a reset link. Use it to set a new password, then log in here.
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 rounded-xl bg-red-500/20 text-red-300 text-sm border border-red-500/30">
                    {error}
                  </div>
                )}
                <p className="text-slate-400 text-sm">Enter your email and we&apos;ll send you a link to set a new password.</p>
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-300 mb-1">Email</label>
                  <input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-2 rounded-xl border border-slate-600 bg-slate-700/50 text-white placeholder-slate-500 focus:ring-2 focus:ring-brand-pink/50 focus:border-brand-pink outline-none"
                    placeholder="you@example.com"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl bg-brand-pink text-white font-semibold hover:bg-brand-pink-dark disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </button>
              </form>
            )}
            <p className="mt-4 text-center">
              <button
                type="button"
                onClick={() => { setShowForgot(false); setError(''); setForgotSent(false); }}
                className="text-slate-400 hover:text-brand-pink text-sm"
              >
                Back to login
              </button>
            </p>
          </>
        )}

        <p className="mt-6 text-center text-slate-400 text-sm">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="text-brand-pink font-semibold hover:underline">
            Register
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
